import { isPossiblePhoneNumber, isValidPhoneNumber, parsePhoneNumber } from 'libphonenumber-js';
import { PortalType } from '@sparrow-card/sparrow-card-constants';
import { EnvironmentType } from 'enums/EnvironmentType';
import { StaticErrorMessage } from 'enums/Messages';
import { getIsRequiredMessage, isMinLength } from './error';
import { isString } from './typeOf';

const environment = import.meta.env.VITE_ENVIRONMENT;

export const removeWhiteSpace = <Value>(value: Value): Value | string => {
  if (isString(value)) {
    return value.replace(/^\s+/, '');
  }

  return value;
};

export const formatMonetary = (value: string) => {
  return value.trim().replace('$', '');
};

const phoneValidate = (value: string, name: string) => {
  if (value) {
    const fullPhoneNumber = `+1${value}`;

    if (!isPossiblePhoneNumber(fullPhoneNumber)) {
      return StaticErrorMessage.InvalidPhoneFormat;
    }

    const possibleCountries = parsePhoneNumber(fullPhoneNumber).getPossibleCountries();

    if (
      !isValidPhoneNumber(fullPhoneNumber) ||
      possibleCountries.length !== 1 ||
      possibleCountries[0] !== 'US'
    ) {
      return StaticErrorMessage.InvalidUSNumber;
    }

    return true;
  }

  return getIsRequiredMessage(name);
};

export const deleteDoubleSpace = (value: string | number) => {
  if (!value) {
    return '';
  }

  return value
    .toString()
    .split(' ')
    .filter((item) => (item === '' ? null : item))
    .join(' ');
};

const datePickerValidate = (value: string, name: string) => {
  const MIN_YEAR = 1902;
  const NUMBER_OF_MONTHS = 12;

  const isValidYear = (year: number) => year <= new Date().getFullYear() && year >= MIN_YEAR;

  const isValidMonth = (month: number) => month > 0 && month <= NUMBER_OF_MONTHS;

  const isValidDate = (day: number, year: number, month: number) => {
    const numberOfDays = new Date(year, month, 0).getDate();

    return day > 0 && day <= numberOfDays;
  };

  if (value) {
    const year = Number(value.slice(-4));
    const month = Number(value.slice(0, 2));
    const day = Number(value.slice(3, 5));
    const checkByCurrentDate =
      year === new Date().getFullYear() &&
      (month > new Date().getMonth() + 1 ||
        (day > new Date().getDate() && month >= new Date().getMonth() + 1));

    if (
      !isValidYear(year) ||
      !isValidMonth(month) ||
      !isValidDate(day, year, month) ||
      checkByCurrentDate
    ) {
      return StaticErrorMessage.InvalidDate;
    }

    return true;
  }

  return getIsRequiredMessage(name);
};

const basicSsnValidate = (value: string, name: string) => {
  if (!value) {
    return getIsRequiredMessage(name);
  }

  if (value.includes('X')) {
    return StaticErrorMessage.InvalidSSN;
  }

  return true;
};

const ssnValidate = (value: string, name: string) => {
  const basicValidate = basicSsnValidate(value, name);

  if (basicValidate !== true) {
    return basicValidate;
  }

  if (environment !== EnvironmentType.Production) {
    // Make additional checks only in production
    return true;
  }

  const start = value.slice(0, 3);
  const middle = value.slice(4, 6);
  const end = value.slice(-4);

  const isValidStart = start !== '000' && start !== '666' && Number(start) < 900;

  const isValidMiddle = middle !== '00';

  const isValidEnd = end !== '0000';

  if (!isValidStart || !isValidMiddle || !isValidEnd) {
    return StaticErrorMessage.InvalidSSN;
  }

  return true;
};

const restrictedDomains = [
  'gnail',
  'gmsil',
  'apl',
  'gail',
  'cloud',
  'man',
  'sol',
  'gma',
  'icould',
  'iclod',
  'ao',
  'gm',
];
const strictDomains = ['gmail', 'yahoo', 'outlook', 'aol', 'hotmail', 'icloud'];
const restrictedExtensions = [
  'con',
  'cim',
  'cok',
  'col',
  'comm',
  'vom',
  'cu',
  'ir',
  'kp',
  'sy',
  'ru',
  'by',
  'mm',
  'cf',
  'cd',
  'iq',
  'lb',
  'ly',
  'ml',
  'ni',
  'so',
  'ss',
  'sd',
  'ua',
  've',
  'ye',
  'zw',
];

export const hasSpecialCharsAfterAt = (email: string): boolean => {
  const atIndex = email.indexOf('@');

  if (atIndex === -1) {
    return false;
  }

  // Check if there's a character right after @
  if (atIndex + 1 >= email.length) {
    return true;
  }

  // Check if the character right after @ is not a letter or digit
  const charAfterAt = email.charAt(atIndex + 1);

  return !/[a-zA-Z0-9]/.test(charAfterAt);
};

export const customEmailValidation = (email: string) => {
  const [, domainPart] = email.split('@');

  if (!domainPart) {
    return false;
  }

  if (hasSpecialCharsAfterAt(email)) {
    return false;
  }

  const [domain, extension] = domainPart.split('.');

  const isError =
    restrictedDomains.includes(domain) ||
    restrictedExtensions.includes(extension) ||
    (strictDomains.includes(domain) && extension !== 'com');

  return !isError;
};
// Detects: PO Box, P.O. Box, P O Box, Post Office Box, Postal Box, PO Bx, P.O.B, etc., with variations in periods/spaces.

const PO_BOX_REGEX =
  /\b(?:P\.?\s?O\.?\s?(?:Box|Bx|B)?|Post(?:al)?\.?\s?Office\.?\s?Box|Post(?:al)?\.?\s?Box)(?:\s|\b)/i;

const streetAddressValidate = (value: string, flow?: PortalType) => {
  // Skip PO Box check for Starling and Reinstatement flows
  if (flow === PortalType.Starling || flow === PortalType.Reinstatement) {
    return true;
  }

  const doesValueIncludesPoBox = PO_BOX_REGEX.test(value);

  if (doesValueIncludesPoBox) {
    return StaticErrorMessage.PoBox;
  }

  return true;
};

export const zipValidate = (value: string, minLength: number) => {
  return String(value).length !== minLength ? isMinLength(minLength) : undefined;
};

export const RegExpCollection = {
  onlyLatin: /^[A-Za-z]+$/,
  email: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
  unit: /[_`'"]|[^A-z0-9.'\-\s]/g,
  poBox: /\bP(ost|ostal)?([ \.]*(O|0)(ffice)?)?([ \.]*(Box|B|Bx))?\b/i,
  firstLastName:
    /^[A-Za-zÁÀÂÃÄÅĀĂĄǍȀȂǺÇĆĈĊČÉÈÊËĒĔĖĘĚȄȆÍÌÎÏĪĬĮİǏȈȊÑŃŇŅṄṆṈṊÓÒÔÕÖŌŎŐǑȌȎǪǬÚÙÛÜŪŬŮŰŲǓȔȖÝŶŸȲŁŚŹŻŽáàâãäåāăąǎȁȃǻçćĉċčéèêëēĕėęěȅȇíìîïīĭįǐȉȋñńňņṅṇṉṋóòôõöōŏőǒȍȏǫǭúùûüūŭůűųǔȕȗýŷÿȳłśźżž' -]+$/,
  streetAddressAndUnit: /^[A-Za-z0-9\s\-\/.#'`]+$/,
  city: /^[A-Za-z\s',-]+$/,
};

export const formValidation = {
  phone: phoneValidate,
  datePicker: datePickerValidate,
  ssn: ssnValidate,
  streetAddress: streetAddressValidate,
  zip: zipValidate,
};
